import { AiInterviewQuestion } from "../../../graphql";

export type SuggestionFieldType =
  | "QUESTION"
  | "FOLLOWUP"
  | "RUBRIC"
  | "QUALIFICATION_CRITERIA"
  | "SUMMARY_CRITERIA";

export type SuggestionFieldPositionMode = "ABSOLUTE_INDEX" | "APPEND";

export type SuggestionFieldTarget = {
  field: SuggestionFieldType;
  index?: number | null;
  positionMode?: SuggestionFieldPositionMode | null;
  changeIndex?: number | null;
};

export type SuggestionFieldChange = SuggestionFieldTarget & {
  value: string;
};

export type GuideQuestionSuggestionV2 = {
  id: string;
  questionId: string;
  questionIndex: number;
  category: string;
  impactScore: number;
  title: string;
  description: string;
  fieldChanges: SuggestionFieldChange[];
};

export type GuideQuestionSuggestionsBucketV2 = {
  questionId: string;
  originalQuestionId: string;
  questionIndex: number;
  questionText: string;
  analysisError?: string | null;
  suggestions: GuideQuestionSuggestionV2[];
};

export type SuggestionConflictReason =
  | "accepted-change-conflict"
  | "selected-suggestion-conflict"
  | "partial-application-lock";

export type QuestionSuggestionPreviewState = {
  suggestionId: string;
  updatedQuestion: AiInterviewQuestion;
  fieldChanges: SuggestionFieldChange[];
  affectedFields: SuggestionFieldTarget[];
  resolvedFields: Record<string, "accepted" | "rejected">;
  acceptedCount: number;
};

const getSuggestionFieldPositionMode = (
  target: Pick<SuggestionFieldTarget, "field" | "positionMode">
): SuggestionFieldPositionMode =>
  target.field === "FOLLOWUP" && target.positionMode === "APPEND"
    ? "APPEND"
    : "ABSOLUTE_INDEX";

export const suggestionFieldKey = ({
  field,
  index,
  positionMode,
  changeIndex,
}: SuggestionFieldTarget): string => {
  const normalizedPositionMode = getSuggestionFieldPositionMode({
    field,
    positionMode,
  });
  if (field === "FOLLOWUP" && normalizedPositionMode === "APPEND") {
    return `${field}:${normalizedPositionMode}:${changeIndex ?? "append"}`;
  }
  return `${field}:${normalizedPositionMode}:${index ?? "all"}`;
};

const isListField = (field: SuggestionFieldType): boolean =>
  field === "FOLLOWUP" || field === "RUBRIC";

export const suggestionFieldsOverlap = (
  left: SuggestionFieldTarget,
  right: SuggestionFieldTarget
): boolean => {
  if (left.field !== right.field) {
    return false;
  }
  if (
    left.field === "FOLLOWUP" &&
    (getSuggestionFieldPositionMode(left) === "APPEND" ||
      getSuggestionFieldPositionMode(right) === "APPEND")
  ) {
    return false;
  }
  if (!isListField(left.field)) {
    return true;
  }
  if (left.index == null || right.index == null) {
    return true;
  }
  return left.index === right.index;
};

export const suggestionTargetsFromChanges = (
  fieldChanges: SuggestionFieldChange[]
): SuggestionFieldTarget[] => {
  const uniqueTargets = new Map<string, SuggestionFieldTarget>();
  fieldChanges.forEach((change, changeIndex) => {
    const normalizedTarget: SuggestionFieldTarget = {
      field: change.field,
      index: change.index ?? null,
      positionMode: getSuggestionFieldPositionMode(change),
      changeIndex:
        change.field === "FOLLOWUP" &&
        getSuggestionFieldPositionMode(change) === "APPEND"
          ? change.changeIndex ?? changeIndex
          : null,
    };
    const key = suggestionFieldKey(normalizedTarget);
    if (!uniqueTargets.has(key)) {
      uniqueTargets.set(key, normalizedTarget);
    }
  });
  return Array.from(uniqueTargets.values());
};

export const suggestionTargetsConflict = (
  leftTargets: SuggestionFieldTarget[],
  rightTargets: SuggestionFieldTarget[]
): boolean =>
  leftTargets.some((left) =>
    rightTargets.some((right) => suggestionFieldsOverlap(left, right))
  );

const normalizeListValues = (
  values: Array<string | null> | undefined | null
): string[] =>
  (values ?? []).map((value) => (typeof value === "string" ? value : ""));

export const prepareSuggestionFieldChangesForPreview = (
  fieldChanges: SuggestionFieldChange[]
): SuggestionFieldChange[] =>
  fieldChanges.map((change, changeIndex) => ({
    ...change,
    positionMode: getSuggestionFieldPositionMode(change),
    changeIndex:
      change.field === "FOLLOWUP" &&
      getSuggestionFieldPositionMode(change) === "APPEND"
        ? change.changeIndex ?? changeIndex
        : null,
  }));

export const applySuggestionFieldChangesToQuestion = (
  question: AiInterviewQuestion,
  fieldChanges: SuggestionFieldChange[]
): AiInterviewQuestion => {
  const updatedQuestion: AiInterviewQuestion = {
    ...question,
    followups: normalizeListValues(question.followups),
    rubric: normalizeListValues(question.rubric),
    qualificationCriteria: question.qualificationCriteria ?? null,
    summaryCriteria: question.summaryCriteria ?? null,
  };

  fieldChanges.forEach((change) => {
    switch (change.field) {
      case "QUESTION":
        updatedQuestion.question = change.value;
        break;
      case "FOLLOWUP": {
        if (getSuggestionFieldPositionMode(change) === "APPEND") {
          updatedQuestion.followups = [
            ...normalizeListValues(updatedQuestion.followups),
            change.value,
          ];
          break;
        }
        if (typeof change.index !== "number" || change.index < 0) {
          return;
        }
        const followups = normalizeListValues(updatedQuestion.followups);
        while (followups.length <= change.index) {
          followups.push("");
        }
        followups[change.index] = change.value;
        updatedQuestion.followups = followups;
        break;
      }
      case "RUBRIC": {
        if (typeof change.index !== "number" || change.index < 0) {
          return;
        }
        const rubric = normalizeListValues(updatedQuestion.rubric);
        while (rubric.length <= change.index) {
          rubric.push("");
        }
        rubric[change.index] = change.value;
        updatedQuestion.rubric = rubric;
        break;
      }
      case "QUALIFICATION_CRITERIA":
        updatedQuestion.qualificationCriteria = change.value;
        break;
      case "SUMMARY_CRITERIA":
        updatedQuestion.summaryCriteria = change.value;
        break;
      default:
        break;
    }
  });

  return updatedQuestion;
};
